import { useEffect, useCallback, useRef } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useLanguage, UI_TRANSLATIONS } from "../LanguageContext";

const TOUR_STORAGE_KEY = "lite-tour-completed";

/**
 * Guided tour for the Lite CMS product journey.
 *
 * Tour Flow:
 *  1. Welcome (no element — center popover)
 *  2. Sidebar navigation
 *  3. Dashboard overview cards
 *  4. Quick actions checklist
 *  5. Display tab → pair screen
 *  6. Library tab → upload content
 *  7. Playlist tab → build & publish
 *  8. Finish — congratulations
 */
const GuidedTour = ({ activeTab, onNavigate, isReady }) => {
  const { lang } = useLanguage();
  const t = (UI_TRANSLATIONS[lang] || UI_TRANSLATIONS.en).guidedTour;
  const driverRef = useRef(null);

  const buildSteps = useCallback(() => {
    // Steps adapt based on which page the user is on
    const steps = [];

    // ── Step 1: Welcome (no element highlight — centered popover) ──
    steps.push({
      popover: {
        title: t.step1Title,
        description: t.step1Desc,
        side: "over",
        align: "center",
      },
    });

    // ── Step 2: Sidebar Navigation ──
    steps.push({
      element: "#lite-sidebar",
      popover: {
        title: t.step2Title,
        description: t.step2Desc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 3: Top Bar ──
    steps.push({
      element: "#lite-topbar",
      popover: {
        title: t.step3Title,
        description: t.step3Desc,
        side: "bottom",
        align: "center",
      },
    });

    // ── Step 4: Dashboard Cards ──
    if (activeTab === "overview") {
      steps.push({
        element: "#lite-stat-display",
        popover: {
          title: t.step4aTitle,
          description: t.step4aDesc,
          side: "bottom",
          align: "center",
        },
      });

      steps.push({
        element: "#lite-stat-library",
        popover: {
          title: t.step4bTitle,
          description: t.step4bDesc,
          side: "bottom",
          align: "center",
        },
      });

      steps.push({
        element: "#lite-stat-playlist",
        popover: {
          title: t.step4cTitle,
          description: t.step4cDesc,
          side: "bottom",
          align: "center",
        },
      });

      steps.push({
        element: "#lite-quick-actions",
        popover: {
          title: t.step4dTitle,
          description: t.step4dDesc,
          side: "bottom",
          align: "center",
        },
      });
    }

    // ── Step 5: Display page navigation prompt ──
    steps.push({
      element: "#lite-nav-screen",
      popover: {
        title: t.step5Title,
        description: t.step5Desc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 6: Library page navigation prompt ──
    steps.push({
      element: "#lite-nav-library",
      popover: {
        title: t.step6Title,
        description: t.step6Desc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 7: Playlist page navigation prompt ──
    steps.push({
      element: "#lite-nav-playlist",
      popover: {
        title: t.step7Title,
        description: t.step7Desc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 8: Pro Upgrade ──
    steps.push({
      element: "#lite-nav-pro",
      popover: {
        title: t.step8Title,
        description: t.step8Desc,
        side: "right",
        align: "center",
      },
    });

    // ── Step 9: Finish ──
    steps.push({
      popover: {
        title: t.step9Title,
        description: t.step9Desc,
        side: "over",
        align: "center",
      },
    });

    return steps;
  }, [activeTab, t]);

  const startTour = useCallback(() => {
    // Destroy existing instance if any
    if (driverRef.current) {
      driverRef.current.destroy();
    }

    // Compute the correct arrow position so it always points at the
    // active element's centre — independent of driver.js viewport adjustments.
    const fixArrow = () => {
      const arrow = document.querySelector(
        "#driver-popover-content .driver-popover-arrow",
      );
      if (!arrow || arrow.classList.contains("driver-popover-arrow-none"))
        return;

      arrow.style.setProperty("border-color", "transparent", "important");

      const popover = document.getElementById("driver-popover-content");
      const activeEl = document.querySelector(".driver-active-element");
      if (!popover || !activeEl) return;

      const pr = popover.getBoundingClientRect();
      const er = activeEl.getBoundingClientRect();

      if (arrow.classList.contains("driver-popover-arrow-side-right")) {
        arrow.style.setProperty("border-right-color", "#ffffff", "important");
        const top = Math.max(
          12,
          Math.min(pr.height - 22, er.top + er.height / 2 - pr.top - 5),
        );
        arrow.style.setProperty("top", `${top}px`, "important");
        arrow.style.setProperty("bottom", "auto", "important");
        arrow.style.setProperty("margin-top", "0", "important");
      } else if (arrow.classList.contains("driver-popover-arrow-side-left")) {
        arrow.style.setProperty("border-left-color", "#ffffff", "important");
        const top = Math.max(
          12,
          Math.min(pr.height - 22, er.top + er.height / 2 - pr.top - 5),
        );
        arrow.style.setProperty("top", `${top}px`, "important");
        arrow.style.setProperty("bottom", "auto", "important");
        arrow.style.setProperty("margin-top", "0", "important");
      } else if (arrow.classList.contains("driver-popover-arrow-side-bottom")) {
        arrow.style.setProperty("border-bottom-color", "#ffffff", "important");
        const left = Math.max(
          12,
          Math.min(pr.width - 22, er.left + er.width / 2 - pr.left - 5),
        );
        arrow.style.setProperty("left", `${left}px`, "important");
        arrow.style.setProperty("right", "auto", "important");
        arrow.style.setProperty("margin-left", "0", "important");
      } else if (arrow.classList.contains("driver-popover-arrow-side-top")) {
        arrow.style.setProperty("border-top-color", "#ffffff", "important");
        const left = Math.max(
          12,
          Math.min(pr.width - 22, er.left + er.width / 2 - pr.left - 5),
        );
        arrow.style.setProperty("left", `${left}px`, "important");
        arrow.style.setProperty("right", "auto", "important");
        arrow.style.setProperty("margin-left", "0", "important");
      }
    };

    const driverObj = driver({
      showProgress: true,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayColor: "rgba(0, 0, 0, 0.6)",
      stagePadding: 8,
      stageRadius: 12,
      popoverClass: "lite-tour-popover",
      nextBtnText: t.nextBtn,
      prevBtnText: t.prevBtn,
      doneBtnText: t.doneBtn,
      progressText: t.progressText,
      // Fix arrow on the very first frame it becomes visible (before onHighlighted).
      onPopoverRender: () => requestAnimationFrame(fixArrow),
      // Fix again after animation completes as a failsafe.
      onHighlighted: fixArrow,
      onDestroyStarted: () => {
        driverObj.destroy();
        localStorage.setItem(TOUR_STORAGE_KEY, "true");
      },
      steps: buildSteps(),
    });

    driverRef.current = driverObj;
    driverObj.drive();
    // Mark as seen once the tour actually starts so it only auto-plays once
    // (even if the user doesn't finish it).
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
  }, [buildSteps]);

  // Auto-start tour on first visit — guard with ref so tab changes don't retrigger
  const hasAutoStarted = useRef(false);
  useEffect(() => {
    if (!isReady || hasAutoStarted.current) return;
    if (localStorage.getItem(TOUR_STORAGE_KEY)) {
      hasAutoStarted.current = true;
      return;
    }
    hasAutoStarted.current = true;
    const timer = setTimeout(startTour, 600);
    return () => clearTimeout(timer);
  }, [isReady, startTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);

  // Expose startTour for the help button
  return { startTour };
};

export default GuidedTour;
